import { useCallback, useRef, useState } from "react";

/**
 * Shared image-carousel state & scroll helpers for property cards.
 *
 * Manages a scroll-snap container where each "page" is the full
 * container width (one image at a time).  Returns refs, state, and
 * handlers that both Card.tsx and PropertyCard.tsx consume.
 */
export function useImageCarousel(imageCount: number) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const scrollToImage = useCallback((index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const width = container.offsetWidth;
    container.scrollTo({ left: width * index, behavior: "smooth" });
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const width = container.offsetWidth;
    const newIndex = Math.round(container.scrollLeft / width);
    setActiveImageIndex(newIndex);
  }, []);

  const prevImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const newIndex =
        activeImageIndex === 0 ? imageCount - 1 : activeImageIndex - 1;
      scrollToImage(newIndex);
    },
    [activeImageIndex, imageCount, scrollToImage],
  );

  const nextImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const newIndex =
        activeImageIndex === imageCount - 1 ? 0 : activeImageIndex + 1;
      scrollToImage(newIndex);
    },
    [activeImageIndex, imageCount, scrollToImage],
  );

  return {
    scrollContainerRef,
    activeImageIndex,
    scrollToImage,
    handleScroll,
    prevImage,
    nextImage,
  };
}
